import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError, REMINDER_CATEGORIES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead } from "@/pages/AppLayout";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function LogService() {
  const { id } = useParams();
  const nav = useNavigate();
  const { isFree } = useAuth();
  const [machine, setMachine] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [form, setForm] = useState({ service_type: "service", parts_replaced: "", cost: "", hours: "", km: "", notes: "", photo: "", service_date: new Date().toISOString().slice(0, 10), reminder_id: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get(`/machines/${id}`).then(({ data }) => {
      setMachine(data.machine);
      setReminders(data.reminders);
      setForm((f) => ({ ...f, hours: data.machine.running_hours || "", km: data.machine.odometer_km || "" }));
    });
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/services", {
        machine_id: id,
        service_type: form.service_type,
        parts_replaced: form.parts_replaced,
        cost: form.cost ? parseFloat(form.cost) : 0,
        hours: form.hours !== "" ? parseFloat(form.hours) : null,
        km: form.km !== "" ? parseFloat(form.km) : null,
        notes: form.notes,
        photo: form.photo,
        service_date: form.service_date ? new Date(form.service_date).toISOString() : null,
        reminder_id: form.reminder_id || null,
      });
      toast.success("Service logged");
      nav(`/machines/${id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="p-5 md:p-10 pb-24 max-w-3xl" data-testid="log-service-page">
      <Link to={`/machines/${id}`} className="flex items-center gap-2 text-[#94A3B8] font-body text-sm mb-4"><ArrowLeft className="w-4 h-4" /> Back</Link>
      <PageHead title="Log Service" sub={machine ? `${machine.make} ${machine.model}` : ""} />
      <form onSubmit={submit} className="card p-6 md:p-8 space-y-5" data-testid="service-log-form">
        <div className="grid sm:grid-cols-2 gap-5">
          <div><label className="il-label">Service Type</label>
            <select className="il-input capitalize" value={form.service_type} onChange={set("service_type")} data-testid="service-type">
              {REMINDER_CATEGORIES.map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
            </select>
          </div>
          <div><label className="il-label">Date</label><input className="il-input font-mono" type="date" value={form.service_date} onChange={set("service_date")} data-testid="service-date" /></div>
          <div><label className="il-label">Cost (AUD)</label><input className="il-input font-mono" type="number" step="0.01" value={form.cost} onChange={set("cost")} placeholder="0.00" data-testid="service-cost" /></div>
          <div><label className="il-label">Machine Hours (updates machine)</label><input className="il-input font-mono" type="number" step="0.1" value={form.hours} onChange={set("hours")} data-testid="service-hours" /></div>
          <div><label className="il-label">Odometer km (updates machine)</label><input className="il-input font-mono" type="number" step="1" value={form.km} onChange={set("km")} data-testid="service-km" /></div>
        </div>
        <div><label className="il-label">Parts Replaced</label><input className="il-input" value={form.parts_replaced} onChange={set("parts_replaced")} placeholder="Oil filter, air filter…" data-testid="service-parts" /></div>
        {reminders.length > 0 && (
          <div><label className="il-label">Advance Reminder (optional)</label>
            <select className="il-input" value={form.reminder_id} onChange={set("reminder_id")} data-testid="service-reminder">
              <option value="">— none —</option>
              {reminders.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
            </select>
          </div>
        )}
        {!isFree && <div><label className="il-label">Photo URL</label><input className="il-input" value={form.photo} onChange={set("photo")} placeholder="https://…" data-testid="service-photo" /></div>}
        <div><label className="il-label">Notes</label><textarea className="il-input py-3" style={{ height: "auto", minHeight: "6rem" }} value={form.notes} onChange={set("notes")} data-testid="service-notes" /></div>
        <button className="btn-signal w-full h-14 font-head text-lg" disabled={loading} data-testid="service-submit">{loading ? "Saving…" : "Log Service"}</button>
      </form>
    </div>
  );
}
